import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Download, FileCheck2, Search, AlertTriangle, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { api, API_BASE_URL, downloadBlob, tokenStore } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { toCommaList } from '@/utils/search-list';

/**
 * POD download — one screen, two capabilities.
 *
 *  • **Internal staff** paste up to 100 AWB / LR numbers, check availability, and download the lot
 *    as a ZIP. Gated by the `Bulk POD` permission module (view = look up / open one,
 *    export = the ZIP); SUPER_ADMIN and ADMIN bypass.
 *  • **Merchants** get the same lookup but download **one shipment at a time** — the ZIP button is
 *    hidden and the API refuses `/pod/bulk-zip` for them outright.
 *
 * A merchant is pinned to their own shipments by the API either way, so one page serves both.
 */

const MAX = 100;

type PodStatus = 'READY' | 'NOT_FOUND' | 'NOT_DELIVERED' | 'NO_POD' | 'ERROR';

interface PodRow {
  number: string;
  orderId?: string;
  orderNo?: string;
  awbNo?: string | null;
  lrNo?: string | null;
  courierName?: string;
  isOwnCourier?: boolean;
  customerName?: string;
  deliveredAt?: string | null;
  status: PodStatus;
  source?: 'COURIER_API' | 'GAINT_GENERATED' | 'UPLOADED';
  message?: string;
}

const STATUS_STYLE: Record<PodStatus, string> = {
  READY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  NOT_FOUND: 'bg-slate-100 text-slate-600 border-slate-200',
  NOT_DELIVERED: 'bg-amber-50 text-amber-700 border-amber-200',
  NO_POD: 'bg-rose-50 text-rose-700 border-rose-200',
  ERROR: 'bg-rose-50 text-rose-700 border-rose-200',
};
const STATUS_LABEL: Record<PodStatus, string> = {
  READY: 'Available',
  NOT_FOUND: 'Not found',
  NOT_DELIVERED: 'Not delivered',
  NO_POD: 'No POD',
  ERROR: 'Error',
};
// `GAINT_GENERATED` now covers TWO cases — the OTP certificate for our own network, and a POD we
// generate for a third-party carrier that issues none — so the label stays neutral and the row's
// own `message` says which one it is.
const SOURCE_LABEL: Record<string, string> = {
  COURIER_API: 'From courier',
  GAINT_GENERATED: 'Generated by Gaint',
  UPLOADED: 'On file',
};

export function BulkPodPage() {
  // Bulk (ZIP) download is internal-staff only — a merchant opens PODs one shipment at a time. The
  // server refuses `/pod/bulk-zip` for merchant identities regardless of what the UI shows; hiding
  // the button here just avoids offering an action that would only ever come back 403.
  const { user: _au } = useAuth();
  const isMerchant = _au?.kind === 'merchant';
  const [orderType, setOrderType] = useState<'B2C' | 'B2B'>('B2C');
  const [text, setText] = useState('');
  const [rows, setRows] = useState<PodRow[] | null>(null);
  const [zipping, setZipping] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);

  // Count exactly the way the backend parses: commas are documented, but pasted columns arrive with
  // newlines/tabs, and the same number twice should count once.
  const numbers = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const piece of text.split(/[\s,;\n\r\t]+/)) {
      const v = piece.trim();
      if (!v) continue;
      const key = v.toUpperCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(v);
    }
    return out;
  }, [text]);

  const overLimit = numbers.length > MAX;

  const check = useMutation({
    mutationFn: async () => (await api.post('/pod/lookup', { orderType, numbers: text })).data,
    onSuccess: (d: any) => {
      setRows(d.rows ?? []);
      if (!d.ready) toast.error('No PODs are available for these numbers');
      else toast.success(`${d.ready} of ${d.entered} POD(s) available`);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Lookup failed'),
  });

  const readyCount = (rows ?? []).filter((r) => r.status === 'READY').length;

  const downloadZip = async () => {
    if (!numbers.length) { toast.error('Enter at least one AWB / LR number'); return; }
    if (overLimit) { toast.error(`Maximum ${MAX} numbers at a time`); return; }
    setZipping(true);
    try {
      // Blob download rather than a plain link: the endpoint is a POST and needs the auth header.
      const res = await fetch(`${API_BASE_URL}/pod/bulk-zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenStore.get()}` },
        body: JSON.stringify({ orderType, numbers: text }),
      });
      if (!res.ok) throw new Error((await res.text()).slice(0, 200) || `HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pods-${orderType}-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast.success('POD ZIP downloaded — check _NOT_INCLUDED.txt inside for anything missing');
    } catch (e: any) {
      toast.error(e?.message ?? 'ZIP download failed');
    } finally { setZipping(false); }
  };

  const downloadOne = async (row: PodRow) => {
    setDownloading(row.number);
    try {
      // Saves the file rather than opening it — the button used to be labelled "Open" for exactly
      // that reason. Streamed through our API so the raw uploaded image lands on disk.
      await downloadBlob(`/pod/download?orderType=${orderType}&number=${encodeURIComponent(row.number)}`, row.number);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Download failed');
    } finally { setDownloading(null); }
  };

  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <FileCheck2 size={22} /> {isMerchant ? 'POD Download' : 'Bulk POD Download'}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {isMerchant
            ? `Paste up to ${MAX} AWB / LR numbers to check which proofs of delivery are available, then open them one at a time.`
            : `Paste up to ${MAX} AWB / LR numbers and download every proof of delivery in one ZIP.`}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        {/* Shipment type — decides which set of numbers we look in */}
        <div>
          <div className="text-xs uppercase font-medium tracking-wide text-slate-500 mb-2">Shipment Type *</div>
          <div className="flex gap-2">
            {(['B2C', 'B2B'] as const).map((t) => (
              <button key={t} onClick={() => { setOrderType(t); setRows(null); }}
                className={`px-5 py-2 rounded-lg border text-sm font-medium transition ${orderType === t
                  ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                {t === 'B2C' ? 'B2C (AWB numbers)' : 'B2B (LR numbers)'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs uppercase font-medium tracking-wide text-slate-500">
              {orderType === 'B2B' ? 'LR / AWB numbers' : 'AWB numbers'} — comma separated
            </label>
            <span className={`text-xs font-medium px-2 py-0.5 rounded ${overLimit ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>
              {numbers.length} / {MAX} entered
            </span>
          </div>
          <textarea
            value={text}
            // Spaces and line breaks between numbers turn into commas as you type or paste.
            onChange={(e) => { setText(toCommaList(e.target.value)); setRows(null); }}
            rows={8}
            placeholder={orderType === 'B2B'
              ? 'e.g. 312497641, 312360288, 312265104'
              : 'e.g. GNT0010000123, 24003910060196, D1017569485'}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
          <div className="text-[11px] text-slate-400 mt-1">
            Type or paste them any way — spaces, new lines or a pasted Excel column turn into commas as you go. Duplicates are counted once.
            {overLimit && <span className="text-rose-600 font-medium"> — remove {numbers.length - MAX} to stay within the limit.</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button onClick={() => check.mutate()} disabled={check.isPending || !numbers.length || overLimit}
            className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2">
            {check.isPending ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check availability
          </button>
          {!isMerchant && (
            <button onClick={downloadZip} disabled={zipping || !numbers.length || overLimit}
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2">
              {zipping ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {zipping ? 'Preparing ZIP…' : `Download PODs${rows ? ` (${readyCount})` : ''}`}
            </button>
          )}
          {rows && (
            <span className="text-xs text-slate-500 ml-1">
              {readyCount} available · {rows.length - readyCount} unavailable
              {isMerchant && readyCount > 0 && ' — use Open on each row to download'}
            </span>
          )}
        </div>

        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1">
          <div><b>Where each POD comes from</b> — the row's own note tells you which of these applied:</div>
          <div>• <b>From courier</b> — the carrier's own signed POD, fetched from their API. Always used
            when one exists (today: Delhivery B2B).</div>
          <div>• <b>Generated by Gaint</b> — for a carrier that publishes no POD API, we issue one from
            the carrier's delivery scan: shipment and consignee details, the carrier's own final-status
            wording, and the <i>[Delivered]</i> stamp. For a <b>Gaint</b> network shipment it is the
            OTP-verified certificate with the masked mobile and the delivery photo.</div>
          <div>• <b>On file</b> — a POD already stored against the shipment (courier POD fetched
            earlier, or a field-app upload).</div>
          <div className="text-slate-400 pt-0.5">Anything that cannot be included is listed inside the ZIP
            as <code>_NOT_INCLUDED.txt</code> rather than failing the whole download.</div>
        </div>
      </div>

      {rows && rows.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2.5">Number</th>
                  <th className="text-left px-3 py-2.5">Order No</th>
                  <th className="text-left px-3 py-2.5">Customer</th>
                  <th className="text-left px-3 py-2.5">Courier</th>
                  <th className="text-left px-3 py-2.5">Delivered</th>
                  <th className="text-left px-3 py-2.5">POD</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.number} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-3 py-2 font-mono text-xs text-slate-900">{r.number}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.orderNo ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.customerName ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.courierName ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {r.deliveredAt ? new Date(r.deliveredAt).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.source ? SOURCE_LABEL[r.source] : '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${STATUS_STYLE[r.status]}`}>
                        {r.status === 'READY' ? <CheckCircle2 size={11} />
                          : r.status === 'NOT_DELIVERED' ? <Clock size={11} />
                          : r.status === 'NOT_FOUND' ? <AlertTriangle size={11} /> : <XCircle size={11} />}
                        {STATUS_LABEL[r.status]}
                      </span>
                      {/* Shown on READY rows too — that is where the useful detail lives now
                          ("OTP-verified delivery certificate" vs "Generated POD — this courier
                          issues none" vs "Will be fetched from the courier"). Previously it was
                          hidden unless the row had FAILED, so the operator could not tell what
                          they were about to download. */}
                      {r.message && (
                        <div className="text-[10px] text-slate-400 mt-0.5">{r.message}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.status === 'READY' && (
                        <button onClick={() => downloadOne(r)} disabled={downloading === r.number}
                          className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
                          {downloading === r.number ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Open
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
